import { Dialog, DialogContent, DialogFooter, Button } from "@mindstonehq/ui";
import calendarDownloadExample from "~/assets/calendar_download_example.png";
import outlookCalendarDemo from "~/assets/outlook_calendar_demo.gif";
import { useCustomerSupport } from "~/hooks/customer-support";
import { ICSEvent } from "~/utils/ics-file-utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

interface CalendarExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  events: ICSEvent[];
  programName: string;
}

export function CalendarExportDialog({
  isOpen,
  onClose,
  events,
  programName,
}: CalendarExportDialogProps) {
  const { t } = useLingui();
  const support = useCustomerSupport();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[640px] overflow-y-auto">
        <div className="space-y-2 mb-4">
          <div className="text-foreground text-lg font-semibold leading-7">
            <Trans>How to add live sessions to your Calendar</Trans>
          </div>
          <div className="text-muted-foreground text-sm font-normal leading-tight">
            <Trans>We'll guide you through this step by step.</Trans>
          </div>
        </div>
        <div className="space-y-2 mb-4">
          <div className="text-foreground text-base font-medium leading-normal">
            <Trans>If you are using Google Calendar</Trans>
          </div>
          <div className="text-muted-foreground text-sm font-normal leading-normal">
            <Trans>
              A calendar file has been automatically downloaded. Click on{" "}
              <a
                href="https://support.google.com/calendar/answer/37118"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                this link
              </a>{" "}
              to view what to do with it.
            </Trans>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 relative">
          <div className="space-y-2 sm:pr-4">
            <div className="text-foreground text-base font-medium leading-normal">
              <Trans>
                If you are using an app on your computer (e.g. Outlook, Apple
                Calendar)
              </Trans>
            </div>
            <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
              <li>
                <Trans>
                  Double-click the downloaded file to add the event to your
                  calendar app.
                </Trans>
              </li>
              <li><Trans>That's it! You're all set.</Trans></li>
            </ol>
            <div className="mt-4 ml-4">
              <img
                src={calendarDownloadExample}
                alt={t(msg`Calendar download example`)}
                width="260"
                height="70"
                className="rounded-md"
              />
            </div>
          </div>
          <div className="hidden sm:block absolute left-1/2 top-0 bottom-0 w-px bg-border"></div>
          <div className="space-y-2 sm:pl-4">
            <div className="text-foreground text-base font-medium leading-normal">
              <Trans>If you're using Outlook in a web browser</Trans>
            </div>
            <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
              <li><Trans>Open Outlook in your web browser.</Trans></li>
              <li>
                <Trans>
                  Drag and drop the downloaded <b>.ics</b> file into the browser
                  window.
                </Trans>
              </li>
              <li><Trans>Click "Import" to confirm, and you're done!</Trans></li>
            </ol>
            <div className="mt-4 ml-4">
              <img
                src={outlookCalendarDemo}
                alt={t(msg`Outlook calendar demo`)}
                width="300"
                height="200"
                className="rounded-md"
              />
            </div>
          </div>
        </div>
        <DialogFooter className="mt-6">
          {support.available && (
            <Button variant="outline" onClick={support.open}>
              <Trans>Help</Trans>
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            <Trans>Close</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
